import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import type { ScheduledReportOption, ScheduledReportOptions } from "api/models";
import DatePicker from "components/v2/DatePicker/DatePicker";
import DualListField from "components/v2/DualList/DualListField";
import Input from "components/v2/Input/Input";
import Select from "components/v2/Select/Select";
import type { SelectOption } from "components/v2/Select/types";
import TimeInput from "components/v2/TimeInput/TimeInput";
import {
  BILLING_TYPE,
  CUSTOM_RANGE,
  DAILY_FREQUENCY,
  SHIPMENT_DETAIL_TYPE,
  findInvalidAdditionalEmail,
  getShipmentDetailOptionId,
} from "./scheduledReportHelpers";
import { ScheduledReportFormValues } from "./scheduledReportTypes";
import "./ScheduledReports.scss";

const toSelectOptions = (options: ScheduledReportOption[] = []): SelectOption[] =>
  options.map((option) => ({
    label: option.label,
    value: String(option.value),
  }));

function ScheduledReportFormFields({
  options,
  isProvider,
  isEdit,
}: {
  options: ScheduledReportOptions;
  isProvider: boolean;
  isEdit: boolean;
}) {
  const intl = useIntl();
  const { watch, setValue, getValues } = useFormContext<ScheduledReportFormValues>();
  const dateRangeTouched = useRef(isEdit);
  const reportType = watch("report_type");
  const dataRange = watch("data_range");
  const frequency = watch("report_frequency");
  const isBilling = reportType === BILLING_TYPE;
  const isShipmentDetail = reportType === SHIPMENT_DETAIL_TYPE;

  useEffect(() => {
    if (dateRangeTouched.current) return;
    setValue("data_range", String(frequency) === DAILY_FREQUENCY ? "yesterday" : "this_week");
  }, [frequency, setValue]);

  useEffect(() => {
    if (isBilling) {
      setValue("filter_date_range_by", "book_date");
    }
  }, [isBilling, setValue]);

  return (
    <div className="scheduled-report-form">
      <Input
        name="report_name"
        required
        label={intl.formatMessage({ id: "report.reportName", defaultMessage: "Report name" })}
      />
      <Select
        name="report_type"
        required
        label={intl.formatMessage({ id: "report.reportType", defaultMessage: "Report type" })}
        options={toSelectOptions(options.report_types)}
      />
      <Select
        name="data_range"
        required
        label={intl.formatMessage({ id: "report.dateRange", defaultMessage: "Date range" })}
        options={toSelectOptions(options.date_ranges)}
        onChange={() => {
          dateRangeTouched.current = true;
        }}
      />
      {dataRange === CUSTOM_RANGE && (
        <div className="scheduled-report-form__split">
          <div>
            <label className="required">
              {intl.formatMessage({ id: "report.DateFrom", defaultMessage: "Date from" })}
            </label>
            <DatePicker
              name="data_from"
              selectionMode="single"
              numberOfMonths={1}
              fullWidth
              required
              shouldUnregister
              maxDate={new Date()}
              registerOptions={{ deps: ["data_to"] }}
            />
          </div>
          <div>
            <label className="required">
              {intl.formatMessage({ id: "report.DateTo", defaultMessage: "Date to" })}
            </label>
            <DatePicker
              name="data_to"
              selectionMode="single"
              numberOfMonths={1}
              fullWidth
              required
              shouldUnregister
              maxDate={new Date()}
              registerOptions={{ deps: ["data_from"] }}
              validations={{
                validDate: (value: string) =>
                  !value ||
                  !getValues("data_from") ||
                  value >= (getValues("data_from") || "") ||
                  intl.formatMessage({
                    id: "report.dateValidationMessage",
                    defaultMessage: "End date cannot be less than start date",
                  }),
              }}
            />
          </div>
        </div>
      )}
      {!isProvider && !isBilling && (
        <Select
          name="analytics_filter_by"
          label={intl.formatMessage({ id: "report.displayRange", defaultMessage: "Display range" })}
          options={toSelectOptions(options.display_ranges)}
        />
      )}
      <Select
        name="filter_date_range_by"
        label={intl.formatMessage({ id: "report.groupBy", defaultMessage: "Group by" })}
        options={toSelectOptions(options.group_by)}
        isDisabled={isBilling}
      />
      <div className="scheduled-report-form__split">
        <Select
          name="report_frequency"
          required
          label={intl.formatMessage({ id: "report.frequency", defaultMessage: "Frequency" })}
          options={toSelectOptions(options.frequencies)}
        />
        <div>
          <TimeInput
            name="execution_time"
            required
            label={intl.formatMessage({ id: "general.time", defaultMessage: "Time" })}
          />
          {options.timezone && <p className="mb-0 mt-1 text-sm text-tertiary">{options.timezone}</p>}
        </div>
      </div>
      {!isProvider && isShipmentDetail && (
        <DualListField
          name="shipment_detail_fields"
          required
          shouldUnregister
          label={intl.formatMessage({ id: "report.reportDetails", defaultMessage: "Report details" })}
          optionsListLabel={intl.formatMessage({
            id: "report.chooseReportDetails",
            defaultMessage: "Choose report details",
          })}
          selectedListLabel={intl.formatMessage({
            id: "report.selectedDetails",
            defaultMessage: "Selected details",
          })}
          options={options.shipment_detail_fields.map((field) => ({
            label: field.label,
            value: getShipmentDetailOptionId(field),
          }))}
        />
      )}
      {isProvider && isShipmentDetail && (
        <DualListField
          name="included_companies"
          required
          shouldUnregister
          label={intl.formatMessage({ id: "report.includedShippers", defaultMessage: "Included shippers" })}
          optionsListLabel={intl.formatMessage({
            id: "report.chooseShippers",
            defaultMessage: "Choose shippers",
          })}
          selectedListLabel={intl.formatMessage({
            id: "report.selectedShippers",
            defaultMessage: "Selected shippers",
          })}
          options={options.included_shippers.map((shipper) => ({
            label: shipper.company_name,
            value: String(shipper.id),
          }))}
        />
      )}
      <Input
        name="additional_emails"
        label={intl.formatMessage({ id: "report.additionalEmails", defaultMessage: "Additional emails" })}
        hintText={intl.formatMessage({
          id: "report.additionalEmailsHint",
          defaultMessage: "Optional. Comma-separated. The report is always sent to you as well.",
        })}
        placeholder={intl.formatMessage({
          id: "report.additionalEmailsPlaceholder",
          defaultMessage: "email@example.com, other@example.com",
        })}
        registerOptions={{
          validate: (value: string) => {
            const invalid = findInvalidAdditionalEmail(value || "");
            if (!invalid) return true;
            return intl.formatMessage(
              {
                id: "report.invalidEmail",
                defaultMessage: "{email} is not a valid email address.",
              },
              { email: invalid },
            );
          },
        }}
      />
    </div>
  );
}

export default ScheduledReportFormFields;
